import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import CookieToggle from './CookieToggle';

const CookieSettings = () => {
  const { consent, showSettings, closeSettings, giveConsent } = useCookieConsent();
  const { t } = useLanguage();
  
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Load current preferences when modal opens
    if (showSettings && consent) {
      setPreferences({
        essential: consent.essential !== undefined ? consent.essential : true,
        analytics: consent.analytics !== undefined ? consent.analytics : false,
        marketing: consent.marketing !== undefined ? consent.marketing : false,
      });
    }
  }, [showSettings, consent]);

  useEffect(() => {
    // Prevent body scroll when modal is shown
    if (showSettings) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showSettings]);

  if (!showSettings) return null;

  const handleToggle = (category) => {
    if (category === 'essential') return; // Cannot toggle essential
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    giveConsent(preferences);
    closeSettings();
  };

  const handleCancel = () => {
    closeSettings();
  };

  const handleAcceptAll = () => {
    const allEnabled = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allEnabled);
    giveConsent(allEnabled);
    closeSettings();
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setPreferences(onlyEssential);
    giveConsent(onlyEssential);
    closeSettings();
  };

  // Cookie categories configuration
  const categories = [
    {
      id: 'essential',
      icon: 'mdi:shield-check',
      title: t('cookies.settings.essential.title'),
      description: t('cookies.settings.essential.description'),
      cookies: t('cookies.settings.essential.cookies'),
      disabled: true,
    },
    {
      id: 'analytics',
      icon: 'mdi:chart-line',
      title: t('cookies.settings.analytics.title'),
      description: t('cookies.settings.analytics.description'),
      cookies: t('cookies.settings.analytics.cookies'),
      disabled: false,
    },
    {
      id: 'marketing',
      icon: 'mdi:bullhorn',
      title: t('cookies.settings.marketing.title'),
      description: t('cookies.settings.marketing.description'),
      cookies: t('cookies.settings.marketing.cookies'),
      disabled: false,
    },
  ];

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="st-cookie-settings-backdrop" 
        onClick={handleCancel}
        aria-hidden="true"
      />
      
      {/* Settings Modal */}
      <div 
        className="st-cookie-settings-modal" 
        role="dialog" 
        aria-labelledby="cookie-settings-title"
        aria-modal="true"
      >
        <div className="st-cookie-settings-container">
          {/* Header */}
          <div className="st-cookie-settings-header">
            <div className="st-cookie-settings-header-content">
              <Icon icon="mdi:cookie-settings" className="st-cookie-settings-icon" />
              <h2 id="cookie-settings-title" className="st-cookie-settings-title">
                {t('cookies.settings.title')}
              </h2>
            </div>
            <button 
              onClick={handleCancel}
              className="st-cookie-settings-close"
              aria-label={t('cookies.settings.close')}
            >
              <Icon icon="mdi:close" />
            </button>
          </div>

          {/* Description */}
          <p className="st-cookie-settings-description">
            {t('cookies.settings.description')}{' '}
            <Link to="/cookie-policy" className="st-cookie-settings-link" onClick={closeSettings}>
              {t('cookies.settings.learnMore')}
            </Link>
          </p>

          {/* Cookie Categories */}
          <div className="st-cookie-categories">
            {categories.map((category) => (
              <div 
                key={category.id} 
                className={`st-cookie-category-card ${preferences[category.id] ? 'st-category-active' : ''}`}
              >
                <div className="st-cookie-category-header">
                  <div className="st-cookie-category-info">
                    <Icon 
                      icon={category.icon} 
                      className="st-cookie-category-icon"
                    />
                    <h3 className="st-cookie-category-title">{category.title}</h3>
                  </div>
                  <CookieToggle
                    checked={preferences[category.id]}
                    onChange={() => handleToggle(category.id)}
                    disabled={category.disabled}
                    label={category.title}
                    requiredText={category.id === 'essential' ? t('cookies.settings.essential.required') : undefined}
                  />
                </div>
                
                <p className="st-cookie-category-description">{category.description}</p>
                
                <div className="st-cookie-category-details">
                  <Icon icon="mdi:information-outline" className="st-cookie-details-icon" />
                  <span className="st-cookie-category-cookies">
                    <strong>{t('cookies.settings.cookiesUsed')}:</strong> {category.cookies}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="st-cookie-settings-actions">
            <div className="st-cookie-settings-quick-actions">
              <button 
                onClick={handleAcceptAll}
                className="st-cookie-settings-btn st-cookie-settings-btn-accept-all"
              >
                <Icon icon="mdi:check-all" />
                {t('cookies.settings.acceptAll')}
              </button>
              
              <button 
                onClick={handleRejectAll}
                className="st-cookie-settings-btn st-cookie-settings-btn-reject-all"
              >
                <Icon icon="mdi:close-circle-outline" />
                {t('cookies.settings.rejectAll')}
              </button>
            </div>

            <div className="st-cookie-settings-main-actions">
              <button 
                onClick={handleCancel}
                className="st-cookie-settings-btn st-cookie-settings-btn-cancel"
              >
                {t('cookies.settings.cancel')}
              </button>
              
              <button 
                onClick={handleSave}
                className="st-cookie-settings-btn st-cookie-settings-btn-save"
              >
                <Icon icon="mdi:content-save" />
                {t('cookies.settings.save')}
              </button>
            </div>
          </div>

          {/* Footer Note */}
          <div className="st-cookie-settings-footer">
            <Icon icon="mdi:shield-lock" className="st-cookie-footer-icon" />
            <span className="st-cookie-settings-note">
              {t('cookies.settings.footerNote')}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieSettings;
